import { KeyRound, LogOut, Menu, UserRound } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Avatar } from '@/components/ui/Avatar';
import {
  Dropdown,
  DropdownItem,
  DropdownSeparator,
} from '@/components/ui/Dropdown';
import { AttendancePill } from '@/components/AttendancePill';
import { TopbarClock } from '@/components/TopbarClock';
import { useAuthStore } from '@/store/auth.store';
import { authService } from '@/services/auth.service';

interface TopbarProps {
  onOpenMobile: () => void;
}

export function Topbar({ onOpenMobile }: TopbarProps) {
  const { user, tokens, clear } = useAuthStore();
  const navigate = useNavigate();

  const onLogout = async () => {
    try {
      await authService.logout(tokens?.refreshToken);
    } catch {
      // ignore — clear locally anyway
    }
    clear();
    toast.success('Signed out');
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border/60 bg-white/70 px-4 backdrop-blur-xl backdrop-saturate-150 sm:px-6">
      <button
        onClick={onOpenMobile}
        className="rounded-xl p-2 text-muted-foreground hover:bg-secondary hover:text-foreground lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <TopbarClock />

      <div className="flex flex-1 items-center justify-end gap-1.5">
        <div className="hidden sm:block">
          <AttendancePill />
        </div>

        <Dropdown
          trigger={
            <motion.button
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2.5 rounded-xl px-1.5 py-1 transition-colors hover:bg-secondary"
            >
              <Avatar name={user?.name ?? 'User'} size="sm" />
              <div className="hidden text-left sm:block">
                <p className="text-sm font-semibold leading-tight text-foreground">
                  {user?.name ?? 'User'}
                </p>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {user?.role}
                </p>
              </div>
            </motion.button>
          }
        >
          <div className="flex items-center gap-3 px-3 py-3">
            <Avatar name={user?.name ?? 'User'} size="md" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {user?.name}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </div>
          <DropdownSeparator />
          <DropdownItem
            icon={<UserRound />}
            onClick={() => navigate('/profile')}
          >
            My profile
          </DropdownItem>
          <DropdownItem
            icon={<KeyRound />}
            onClick={() => navigate('/change-password')}
          >
            Change password
          </DropdownItem>
          <DropdownSeparator />
          <DropdownItem icon={<LogOut />} danger onClick={onLogout}>
            Sign out
          </DropdownItem>
        </Dropdown>
      </div>
    </header>
  );
}
